export interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface OpenAIStreamConfig {
  messages: ChatMessage[]
  onToken: (token: string) => void
  onComplete: () => void
  onError: (error: Error) => void
}

export async function streamOpenAIResponse({
  messages,
  onToken,
  onComplete,
  onError,
}: OpenAIStreamConfig) {
  try {
    const response = await fetch(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages,
          stream: true,
        }),
      },
    )

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`)
    }

    if (!response.body) {
      throw new Error('Response body is empty')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const chunk = decoder.decode(value, { stream: true })
      buffer += chunk
      const lines = buffer.split('\n')

      // Keep the last line in the buffer if it's incomplete
      buffer = lines.pop() || ''

      for (const line of lines) {
        const trimmedLine = line.trim()
        if (!trimmedLine || !trimmedLine.startsWith('data: ')) continue

        const data = trimmedLine.slice(6)
        if (data === '[DONE]') {
          onComplete()
          return
        }

        try {
          const json = JSON.parse(data)
          const token = json.choices[0]?.delta?.content || ''
          if (token) {
            onToken(token)
          }
        } catch (e) {
          console.error('Error parsing SSE data:', e)
        }
      }
    }

    // Process any remaining buffer
    if (
      buffer.trim().startsWith('data: ') &&
      buffer.trim() !== 'data: [DONE]'
    ) {
      try {
        const data = buffer.trim().slice(6)
        const json = JSON.parse(data)
        const token = json.choices[0]?.delta?.content || ''
        if (token) onToken(token)
      } catch (e) {
        // ignore incomplete json at very end
      }
    }

    onComplete()
  } catch (error) {
    console.error('OpenAI Stream Error:', error)
    onError(
      error instanceof Error ? error : new Error('Unknown error occurred'),
    )
  }
}
